import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Settings, Bell, Truck, Route, Package, Clock, Shield, Save, RefreshCw
} from 'lucide-react';

const LogisticsSettings = () => {
  const [settings, setSettings] = useState({
    notifications: {
      requestCreated: true,
      requestApproved: true,
      deliveryCompleted: true,
      vehicleMaintenanceDue: true,
      driverLicenseExpiring: true,
      routeOptimized: true,
    },
    operations: {
      autoOptimizeRoutes: true,
      requireApprovalThreshold: 5000, // SAR
      requireApprovalDistance: 500, // km
      enableLoadConsolidation: true,
      enableCubing: true,
      enableEPOD: true,
      enableChainOfCustody: true,
    },
    fleet: {
      maintenanceReminderDays: 7,
      fuelLevelAlert: 25, // percentage
      enableGPS: true,
      enableRealTimeTracking: true,
      autoAssignDrivers: false,
    },
    integration: {
      autoCreateFromWMS: true,
      autoUpdateOrderStatus: true,
      enableWMSIntegration: true,
      enableOMIntegration: true,
    },
  });

  const [activeSection, setActiveSection] = useState('notifications');

  const handleSave = () => {
    alert('Settings saved successfully!');
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset all settings to defaults?')) {
      alert('Settings reset to defaults');
    }
  };

  const sections = [
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'operations', label: 'Operations', icon: Package },
    { id: 'fleet', label: 'Fleet', icon: Truck },
    { id: 'integration', label: 'Integration', icon: Shield },
  ];

  const updateSetting = (category, key, value) => {
    setSettings({
      ...settings,
      [category]: {
        ...settings[category],
        [key]: value,
      },
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Logistics Settings</h2>
            <p className="text-sm text-gray-600 mt-1">Configure logistics operations and preferences</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleReset}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm"
            >
              <RefreshCw className="w-4 h-4" />
              Reset
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm"
            >
              <Save className="w-4 h-4" />
              Save Changes
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 space-y-2">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    activeSection === section.id
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {section.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            {/* Notifications */}
            {activeSection === 'notifications' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Notification Settings</h3>
                {Object.entries(settings.notifications).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">
                        {key === 'requestCreated' && 'Notify when a new logistics request is created'}
                        {key === 'requestApproved' && 'Notify when a request is approved or rejected'}
                        {key === 'deliveryCompleted' && 'Notify when a delivery is completed'}
                        {key === 'vehicleMaintenanceDue' && 'Notify when vehicle maintenance is due'}
                        {key === 'driverLicenseExpiring' && 'Notify when driver licenses are expiring'}
                        {key === 'routeOptimized' && 'Notify when routes are optimized'}
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={value}
                        onChange={(e) => updateSetting('notifications', key, e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                ))}
              </div>
            )}

            {/* Operations */}
            {activeSection === 'operations' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Operations Settings</h3>
                {Object.entries(settings.operations).map(([key, value]) => (
                  <div key={key} className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-medium text-gray-900">
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      </p>
                      {typeof value === 'boolean' ? (
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={value}
                            onChange={(e) => updateSetting('operations', key, e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      ) : (
                        <input
                          type="number"
                          value={value}
                          onChange={(e) => updateSetting('operations', key, parseFloat(e.target.value))}
                          className="w-32 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      )}
                    </div>
                    <p className="text-sm text-gray-600">
                      {key === 'requireApprovalThreshold' && 'Requests above this value (SAR) require approval'}
                      {key === 'requireApprovalDistance' && 'Requests above this distance (km) require approval'}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* Fleet */}
            {activeSection === 'fleet' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Fleet Settings</h3>
                {Object.entries(settings.fleet).map(([key, value]) => (
                  <div key={key} className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-medium text-gray-900">
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      </p>
                      {typeof value === 'boolean' ? (
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={value}
                            onChange={(e) => updateSetting('fleet', key, e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      ) : (
                        <input
                          type="number"
                          value={value}
                          onChange={(e) => updateSetting('fleet', key, parseFloat(e.target.value))}
                          className="w-32 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      )}
                    </div>
                    <p className="text-sm text-gray-600">
                      {key === 'maintenanceReminderDays' && 'Days before maintenance due to send reminder'}
                      {key === 'fuelLevelAlert' && 'Alert when fuel level drops below this percentage'}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* Integration */}
            {activeSection === 'integration' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Integration Settings</h3>
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                  <p className="text-sm text-gray-700">
                    <strong>WMS Integration:</strong> When WMS finishes packing a pallet, automatically create a Logistics Task.
                  </p>
                  <p className="text-sm text-gray-700 mt-2">
                    <strong>Order Management Integration:</strong> When driver captures ePOD for client delivery, automatically update Order status to "Delivered" and trigger Final Invoice.
                  </p>
                </div>
                {Object.entries(settings.integration).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">
                        {key === 'autoCreateFromWMS' && 'Automatically create logistics requests from WMS packing completion'}
                        {key === 'autoUpdateOrderStatus' && 'Automatically update order status when ePOD is captured'}
                        {key === 'enableWMSIntegration' && 'Enable integration with Warehouse Management System'}
                        {key === 'enableOMIntegration' && 'Enable integration with Order Management'}
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={value}
                        onChange={(e) => updateSetting('integration', key, e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogisticsSettings;
